import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormLabel,
  TextField,
} from '@mui/material';
import { CommonDialog, CommonDialogHeader } from '..';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { IAdminUser, IRootState } from '../../../types/store';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { setRolesAndPermissions } from '../../../store/Reducers/RolesAndPermissionsSlice';
import { AdminUsersService } from '../../../api/services/adminUsersService';
import { RolesAndPermissionsService } from '../../../api/services/rolesAndPermissionsService';
import { IUpdateUserRequest } from '../../../types/api/request/adminusersrequest';

interface Props {
  selectedUser: IAdminUser;
  isOpenDialog: boolean;
  setIsOpenDialog: (open: boolean) => void;
  onUpdated: (user: IAdminUser) => void;
}

export const EditAdminUserDialog = ({
  selectedUser,
  isOpenDialog,
  setIsOpenDialog,
  onUpdated,
}: Props) => {
  const dispatch = useDispatch();
  const { content: roles } = useSelector((state: IRootState) => state.rolesAndPermissions);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<IUpdateUserRequest>({
    defaultValues: {
      id: selectedUser.id,
      email: selectedUser.email,
      first_name: selectedUser.first_name,
      last_name: selectedUser.last_name,
      phone_number: selectedUser.phone_number ?? '',
      role_ids: selectedUser.roles.map((r) => r.id),
    },
  });

  useEffect(() => {
    if (!isOpenDialog) return;
    reset({
      id: selectedUser.id,
      email: selectedUser.email,
      first_name: selectedUser.first_name,
      last_name: selectedUser.last_name,
      phone_number: selectedUser.phone_number ?? '',
      role_ids: selectedUser.roles.map((r) => r.id),
    });
  }, [isOpenDialog, selectedUser, reset]);

  useEffect(() => {
    if (!isOpenDialog || roles.length > 0) return;

    let cancelled = false;
    setIsLoadingRoles(true);
    RolesAndPermissionsService.getAllRoles()
      .then((res) => {
        if (cancelled) return;
        dispatch(setRolesAndPermissions(res));
      })
      .catch((error: any) => {
        if (cancelled) return;
        dispatch(
          showNotification({
            message: error?.response?.data?.message || error?.message || 'Failed to load roles.',
            severity: 'error',
          }),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRoles(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpenDialog, roles.length, dispatch]);

  const onSubmit = async (data: IUpdateUserRequest) => {
    try {
      await AdminUsersService.updateUser(data);
      const updated = await AdminUsersService.getAdminUser(selectedUser.id);
      onUpdated(updated);
      dispatch(showNotification({ message: `User "${selectedUser.username}" updated.` }));
      reset();
      setIsOpenDialog(false);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: error?.response?.data?.message || error?.message || 'Failed to update user.',
          severity: 'error',
        }),
      );
    }
  };

  return (
    <CommonDialog
      isOpen={isOpenDialog}
      header={
        <CommonDialogHeader
          title={`Edit User — ${selectedUser.username}`}
          onClose={() => setIsOpenDialog(false)}
        />
      }
      body={
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
        >
          <input type="hidden" {...register('id')} />

          <FormControl>
            <FormLabel>Email</FormLabel>
            <TextField
              type="email"
              fullWidth
              variant="outlined"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email', { required: 'Email is required' })}
            />
          </FormControl>

          <FormControl>
            <FormLabel>First Name</FormLabel>
            <TextField
              type="text"
              fullWidth
              variant="outlined"
              error={!!errors.first_name}
              helperText={errors.first_name?.message}
              {...register('first_name', { required: 'First name is required' })}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Last Name</FormLabel>
            <TextField
              type="text"
              fullWidth
              variant="outlined"
              error={!!errors.last_name}
              helperText={errors.last_name?.message}
              {...register('last_name', { required: 'Last name is required' })}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Phone Number</FormLabel>
            <TextField
              type="text"
              fullWidth
              variant="outlined"
              error={!!errors.phone_number}
              helperText={errors.phone_number?.message}
              {...register('phone_number')}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Roles</FormLabel>
            <Controller
              name="role_ids"
              control={control}
              rules={{ required: 'At least one role is required' }}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  options={roles}
                  loading={isLoadingRoles}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  value={roles.filter((r) => value?.includes(r.id))}
                  onChange={(_, selectedOptions) =>
                    onChange(selectedOptions.map((option) => option.id))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select roles"
                      error={!!errors.role_ids}
                      helperText={errors.role_ids?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingRoles ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ backgroundColor: '#000' }}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      }
      footer={undefined}
    />
  );
};
